import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, ArrowLeft, Send, CheckCircle2, Zap, ShieldCheck, Banknote, MapPin, TrendingUp, Sparkles, Quote } from "lucide-react";

const REGIONS = ["Finistère", "Côtes-d'Armor", "Morbihan", "Ille-et-Vilaine"];
const PARTNER_TYPES = [
  { v: "conciergerie", l: "Conciergerie complète", d: "Vous prenez en charge la gestion de A à Z (check-in, ménage, linge, urgences)." },
  { v: "menage", l: "Ménage / Femme de ménage", d: "Vous intervenez sur les remises en état entre locations." },
];
const LEGAL_STATUSES = [
  { v: "auto-entrepreneur", l: "Auto-entrepreneur" },
  { v: "cesu", l: "CESU" },
  { v: "micro-entreprise", l: "Micro-entreprise" },
  { v: "sasu", l: "SASU" },
  { v: "sarl", l: "SARL" },
  { v: "association", l: "Association" },
  { v: "autre", l: "Autre" },
];
const SERVICES = [
  { v: "checkin", l: "Accueil voyageurs" },
  { v: "key_handover", l: "Remise des clés" },
  { v: "cleaning", l: "Ménage entre locations" },
  { v: "linen", l: "Fourniture / blanchisserie du linge" },
  { v: "photo", l: "Reportage photo" },
  { v: "maintenance", l: "Petite maintenance" },
  { v: "conciergerie_full", l: "Conciergerie clé en main (24/7)" },
  { v: "garden", l: "Entretien jardin / piscine" },
];
const LANGUAGES = [
  { v: "fr", l: "Français" }, { v: "en", l: "English" }, { v: "br", l: "Brezhoneg" },
  { v: "es", l: "Español" }, { v: "de", l: "Deutsch" }, { v: "it", l: "Italiano" },
];

export default function BecomePartnerPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    partner_type: "menage",
    legal_status: "auto-entrepreneur",
    siret: "",
    regions: [],
    cities: "",
    services: [],
    price_range: "",
    languages: ["fr"],
    description: "",
    insurance: false,
    website: "",
    instagram: "",
    logo: "",
  });

  const [uploadingLogo, setUploadingLogo] = useState(false);

  const onLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, logo: data.path }));
      toast.success("Logo téléchargé");
    } catch (err) {
      toast.error("Échec de l'upload du logo");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const toggleArr = (key, val) => {
    setForm((p) => ({
      ...p,
      [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.regions.length) {
      toast.error("Sélectionnez au moins une zone d'intervention");
      return;
    }
    if (!form.services.length) {
      toast.error("Sélectionnez au moins un service");
      return;
    }
    if (!form.logo) {
      toast.error("Le logo de votre entreprise est obligatoire");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        cities: form.cities.split(",").map((c) => c.trim()).filter(Boolean),
      };
      await api.post("/partners/apply", payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-24 sm:pt-28 min-h-screen bg-white">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 bg-[#0A0A0A] text-white flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">Candidature reçue</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
            Trugarez ! Votre demande est entre nos mains.
          </h1>
          <p className="mt-6 text-neutral-600 leading-relaxed">
            Notre équipe à Beaussais-Sur-Mer examine chaque candidature manuellement. Vous recevrez un retour sous 5 jours ouvrés à l'adresse <strong>{form.email}</strong>.
          </p>
          <p className="mt-4 text-sm text-neutral-500">
            En cas de validation, votre fiche sera publiée dans notre annuaire <Link to="/partenaires" className="underline">/partenaires</Link> et les propriétaires bretons pourront vous contacter directement.
          </p>
          <button onClick={() => navigate("/")} className="mt-10 underline underline-offset-4 font-semibold text-sm">Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #C9986A 0%, transparent 60%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <Link to="/partenaires" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white/70 hover:text-white mb-6">
              <ArrowLeft className="w-3 h-3" /> Annuaire des partenaires
            </Link>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9986A] font-bold mb-3">Programme partenaires MSB</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95]">
              Votre métier, <em className="italic font-light">notre vitrine.</em>
            </h1>
            <p className="mt-7 text-lg text-white/85 max-w-xl leading-relaxed">
              Conciergeries, femmes de ménage indépendantes, prestataires bretons — rejoignez le premier réseau d'hospitalité 100% Bretagne. <strong>Plus d'impayés, plus de relances, plus de prospection</strong> : MSB vous apporte les hôtes et automatise vos virements.
            </p>
            <div className="mt-9 flex flex-wrap gap-4 items-center">
              <a href="#candidature" className="bg-white text-[#0A0A0A] px-7 py-3.5 font-semibold hover:bg-[#C9986A] hover:text-[#0A0A0A] transition-colors flex items-center gap-2">
                Postuler maintenant <Sparkles className="w-4 h-4" />
              </a>
              <div className="text-xs text-white/70">
                <div>Inscription gratuite</div>
                <div>Validation sous 5 jours</div>
              </div>
            </div>
          </div>

          {/* Highlight card → simulateur interactif */}
          <div className="lg:justify-self-end w-full max-w-md">
            <ConciergePayoutSimulator />
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="border-b border-black/10 bg-[#F5F2EC] bg-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-600 font-bold mb-3">Vos galères, finies</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">
            Combien d'heures par mois <em className="italic font-light">à courir après vos paiements ?</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Banknote, t: "Impayés et relances", b: "Les paiements sont prélevés en amont sur la carte du voyageur. Stripe nous envoie l'argent, on vous le reverse automatiquement. Plus une facture qui traîne." },
              { icon: TrendingUp, t: "Prospection commerciale", b: "Les propriétaires bretons vous contactent directement depuis votre fiche annuaire. Ils savent que vous êtes vérifié, assuré, validé par MSB." },
              { icon: MapPin, t: "Saisonnalité", b: "Trop de travail en juillet, plus rien en novembre ? Le filtre par ville/région et l'annuaire MSB lissent votre carnet de commandes à l'année." },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="bg-white border border-black/10 p-7">
                  <div className="w-11 h-11 bg-[#0A0A0A] text-white flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
                  <div className="font-display text-xl font-bold">{b.t}</div>
                  <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{b.b}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">Le process</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">En 4 étapes, vous y êtes.</h2>
            <p className="text-neutral-600 mt-5 max-w-md">Pas de bullshit, pas de jargon. Un parcours pensé pour les pros bretons qui veulent gagner du temps.</p>
          </div>
          <div className="space-y-7">
            {[
              { n: "01", t: "Postulez en 5 minutes", d: "Statut juridique (auto-entrepreneur, CESU, micro, SASU, asso), zones d'intervention, services proposés, expérience. Tout est gratuit." },
              { n: "02", t: "Validation manuelle MSB", d: "Notre équipe basée à Beaussais-Sur-Mer étudie chaque dossier en 5 jours ouvrés. Pas d'algorithme — un vrai humain breton vous lit." },
              { n: "03", t: "Connectez votre compte Stripe", d: "Onboarding Express en 10 minutes (RIB, pièce d'identité). Stripe valide votre identité bancaire — c'est ce qui rassure les hôtes." },
              { n: "04", t: "Recevez réservations & virements", d: "Les propriétaires vous contactent via votre fiche. À chaque séjour payé, votre part atterrit automatiquement sur votre compte bancaire — délai standard 2 jours après check-in." },
            ].map((s, i) => (
              <div key={i} className="flex gap-6 border-b border-black/10 pb-7 last:border-0">
                <div className="font-display text-5xl font-bold text-[#7A1535] leading-none shrink-0">{s.n}</div>
                <div>
                  <div className="font-display text-2xl font-bold">{s.t}</div>
                  <p className="text-neutral-600 mt-2 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIRECT PAYMENT VALUE PROP */}
      <section className="bg-[#0A0A0A] text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Zap className="w-10 h-10 mx-auto text-[#C9986A]" />
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-6 max-w-3xl mx-auto leading-tight">
            Vos paiements, <em className="italic font-light">le jour même.</em>
          </h2>
          <p className="text-white/85 mt-6 max-w-2xl mx-auto leading-relaxed text-lg">
            Pas d'avance, pas de facturation en fin de mois, pas de chèque qui se perd. Le voyageur paie sur Stripe → MSB prélève sa commission → <strong>votre part est virée sur votre compte automatiquement</strong>. Pareil pour le propriétaire. Tout est instantané, transparent et tracé.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12 text-left">
            {[
              { t: "100% Stripe sécurisé", b: "Norme PCI-DSS, 3D Secure, conformité européenne. Aucune donnée bancaire ne transite par MSB ni par vous." },
              { t: "Aucune avance de frais", b: "Vous êtes payé une fois le voyageur arrivé, donc une fois la réservation confirmée. Le risque d'annulation reste sur la plateforme." },
              { t: "Facture & relevés mensuels", b: "Stripe génère vos relevés automatiquement. Idéal pour votre comptable ou votre URSSAF." },
            ].map((b, i) => (
              <div key={i} className="border border-white/20 p-6">
                <div className="font-display text-lg font-bold">{b.t}</div>
                <p className="text-sm text-white/75 mt-2 leading-relaxed">{b.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, t: "Validation humaine", d: "Pas d'IA, pas d'auto-validation. Un membre de notre équipe vous appelle pour valider votre profil." },
            { icon: MapPin, t: "Catalogue 100% Bretagne", d: "Nos hôtes ne sont QUE bretons. Vous restez dans votre secteur d'intervention — pas de Paris, pas de Côte d'Azur." },
            { icon: Sparkles, t: "Votre espace dédié", d: "Dashboard conciergerie : réservations à venir, calendrier ménage, revenus mensuels, contact direct propriétaire." },
          ].map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="border border-black/10 p-6">
                <Icon className="w-6 h-6 mb-4" />
                <div className="font-display text-lg font-bold">{b.t}</div>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{b.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-[#F5F2EC] py-20 border-y border-black/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote className="w-10 h-10 mx-auto text-[#7A1535]" />
          <blockquote className="font-display text-2xl sm:text-3xl font-bold leading-snug mt-6 max-w-3xl mx-auto">
            « Avant je passais 2 jours par mois à relancer les propriétaires pour mes factures. Depuis MSB, mes virements arrivent <em className="italic font-light text-[#7A1535]">le mardi suivant chaque check-in</em>. Je peux enfin me concentrer sur le boulot. »
          </blockquote>
          <div className="mt-7 text-sm text-neutral-600">
            <strong className="font-display text-base text-[#0A0A0A]">Marlène K.</strong> — Conciergerie indépendante, Saint-Malo (35)
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="candidature" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">Votre candidature</div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Rejoignez-nous <em className="italic font-light">dès aujourd'hui.</em>
        </h2>
        <p className="mt-5 text-neutral-600 max-w-2xl leading-relaxed">
          Auto-entrepreneurs, micro-entreprises, CESU, associations — tous les statuts sont les bienvenus tant que le sérieux et la qualité sont au rendez-vous.
        </p>

        <form onSubmit={submit} className="mt-10 border border-black/10 p-7 sm:p-10 space-y-8" data-testid="partner-form">
          {/* Type */}
          <fieldset>
            <legend className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Vous êtes</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PARTNER_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.v}
                  data-testid={`pt-${t.v}`}
                  onClick={() => setForm({ ...form, partner_type: t.v })}
                  className={`text-left p-5 border ${form.partner_type === t.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
                >
                  <div className="font-display text-xl font-bold">{t.l}</div>
                  <div className={`text-xs mt-2 ${form.partner_type === t.v ? "text-white/75" : "text-neutral-500"}`}>{t.d}</div>
                </button>
              ))}
            </div>
          </fieldset>

          {/* Logo upload */}
          <fieldset>
            <legend className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Logo de votre entreprise *</legend>
            <p className="text-xs text-neutral-500 mb-3">Obligatoire — visible sur votre fiche annuaire et auprès des propriétaires (PNG ou JPG, ratio carré conseillé).</p>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border border-black/15 bg-neutral-50 flex items-center justify-center overflow-hidden">
                {form.logo ? (
                  <img src={form.logo.startsWith("http") ? form.logo : `${process.env.REACT_APP_BACKEND_URL}/api/files/${form.logo}`} alt="logo" className="w-full h-full object-cover" data-testid="pn-logo-preview" />
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-400">Aperçu</span>
                )}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 border border-black/15 px-4 py-2 text-sm font-semibold hover:bg-neutral-50" data-testid="pn-logo-upload">
                <input type="file" accept="image/*" onChange={onLogoChange} className="hidden" />
                {uploadingLogo ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : "Choisir un fichier"}
              </label>
              {form.logo && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, logo: "" })}
                  className="text-xs underline text-neutral-500 hover:text-[#9A4D1F]"
                  data-testid="pn-logo-remove"
                >
                  Retirer
                </button>
              )}
            </div>
          </fieldset>

          {/* Identité */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom de l'entreprise *" testid="pn-company">
              <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="inp" />
            </Field>
            <Field label="Nom du contact *" testid="pn-contact">
              <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="inp" />
            </Field>
            <Field label="Email *" testid="pn-email">
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" />
            </Field>
            <Field label="Téléphone *" testid="pn-phone">
              <input required type="tel" placeholder="+33 6 12 34 56 78" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" />
            </Field>
            <Field label="Statut juridique *" testid="pn-legal">
              <select required value={form.legal_status} onChange={(e) => setForm({ ...form, legal_status: e.target.value })} className="inp bg-white">
                {LEGAL_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </Field>
            <Field label="N° SIRET (optionnel)" testid="pn-siret">
              <input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} className="inp" />
            </Field>
          </div>

          {/* Zones */}
          <fieldset>
            <legend className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Zones d'intervention *</legend>
            <div className="flex flex-wrap gap-2 mb-3">
              {REGIONS.map((r) => (
                <button
                  type="button"
                  key={r}
                  data-testid={`pn-region-${r}`}
                  onClick={() => toggleArr("regions", r)}
                  className={`text-xs uppercase tracking-[0.1em] font-bold px-3 py-2 border ${form.regions.includes(r) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Field label="Villes principales (séparées par des virgules)" testid="pn-cities">
              <input value={form.cities} onChange={(e) => setForm({ ...form, cities: e.target.value })} placeholder="Saint-Malo, Dinard, Cancale" className="inp" />
            </Field>
          </fieldset>

          {/* Services */}
          <fieldset>
            <legend className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Services proposés *</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SERVICES.map((s) => (
                <button
                  type="button"
                  key={s.v}
                  data-testid={`pn-svc-${s.v}`}
                  onClick={() => toggleArr("services", s.v)}
                  className={`text-left px-3 py-2 text-sm border ${form.services.includes(s.v) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Tarif indicatif" testid="pn-price">
              <input value={form.price_range} onChange={(e) => setForm({ ...form, price_range: e.target.value })} placeholder="Ex : 25–45€ / passage" className="inp" />
            </Field>
            <Field label="Site web" testid="pn-website">
              <input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className="inp" />
            </Field>
          </div>

          {/* Langues */}
          <fieldset>
            <legend className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Langues parlées</legend>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((L) => (
                <button
                  type="button"
                  key={L.v}
                  data-testid={`pn-lang-${L.v}`}
                  onClick={() => toggleArr("languages", L.v)}
                  className={`text-xs uppercase tracking-[0.1em] font-bold px-3 py-2 border ${form.languages.includes(L.v) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
                >
                  {L.l}
                </button>
              ))}
            </div>
          </fieldset>

          <Field label="Présentez votre activité *" testid="pn-desc">
            <textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Votre histoire, vos années d'expérience, ce qui vous différencie…" className="inp" />
          </Field>

          <label className="flex items-center gap-3 text-sm">
            <input data-testid="pn-insurance" type="checkbox" checked={form.insurance} onChange={(e) => setForm({ ...form, insurance: e.target.checked })} className="w-4 h-4" />
            <span>Je dispose d'une assurance responsabilité civile professionnelle</span>
          </label>

          <div className="pt-3 border-t border-black/10 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              data-testid="pn-submit"
              disabled={sending}
              className="bg-[#0A0A0A] text-white px-8 py-3.5 font-semibold hover:bg-[#1B2A49] transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-4 h-4" /> Envoyer ma candidature</>}
            </button>
            <p className="text-[11px] text-neutral-500 flex-1 min-w-[200px]">Notre équipe étudie chaque dossier manuellement (5 jours ouvrés max). Aucun frais d'inscription.</p>
          </div>
        </form>
      </section>
      <style>{`.inp { width: 100%; border: 1px solid rgba(0,0,0,.15); padding: .75rem; outline: none; font-size: .875rem } .inp:focus { border-color: #0A0A0A }`}</style>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function PayoutRow({ label, value, accent = false, muted = false, small = false }) {
  return (
    <div className={`flex items-baseline justify-between ${muted ? "text-neutral-500" : ""} ${small ? "text-xs" : ""}`}>
      <span>{label}</span>
      <span className={`font-display font-bold ${accent ? "text-[#7A1535] text-xl" : ""}`}>{value}</span>
    </div>
  );
}

/* ============================================================
   Simulateur de revenus conciergerie
   - Slider commission 10–40%
   - Slider prix/nuit 50–400€
   - Slider frais ménage 0–150€
   - Toggle « le ménage me revient »
   - Affiche la décomposition Voyageur / MSB / Conciergerie / Propriétaire
   ============================================================ */
function ConciergePayoutSimulator() {
  const [price, setPrice] = useState(120);
  const [share, setShare] = useState(15);
  const [cleaning, setCleaning] = useState(50);
  const [keepsCleaning, setKeepsCleaning] = useState(true);

  // Modèle de calcul aligné avec server.py /api/bookings/checkout
  // - Le voyageur paie : prix nuit + ménage + taxe (taxe ignorée ici pour la lisibilité)
  // - MSB commission 10% sur le sous-total nuitées (hors ménage)
  // - Conciergerie : share% sur le sous-total nuitées (+ ménage si toggle)
  // - Propriétaire : reste
  const travelerPays = price + cleaning;
  const msbFee = +(price * 0.10).toFixed(2);
  let conciergeCut = +(price * (share / 100)).toFixed(2);
  if (keepsCleaning) conciergeCut = +(conciergeCut + cleaning).toFixed(2);
  const ownerPayout = +(travelerPays - msbFee - conciergeCut).toFixed(2);

  return (
    <div className="bg-white text-[#0A0A0A] p-5 sm:p-7 shadow-2xl" data-testid="concierge-simulator">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7A1535] mb-2">Simulateur</div>
      <div className="font-display text-2xl font-bold leading-tight">
        Calculez vos revenus en direct
      </div>
      <p className="text-xs text-neutral-600 mt-2">Ajustez les curseurs : le partage évolue en temps réel.</p>

      {/* Sliders */}
      <div className="mt-5 space-y-4">
        <SimRow
          label="Prix de la nuit"
          value={`${price}€`}
          min={50} max={400} step={5}
          v={price} onChange={setPrice}
          testid="sim-price"
        />
        <SimRow
          label="Votre commission"
          value={`${share}%`}
          min={10} max={40} step={1}
          v={share} onChange={setShare}
          accent
          testid="sim-share"
        />
        <SimRow
          label="Frais de ménage"
          value={`${cleaning}€`}
          min={0} max={150} step={5}
          v={cleaning} onChange={setCleaning}
          testid="sim-cleaning"
        />
        <label className="flex items-center gap-2 text-xs select-none cursor-pointer pt-1" data-testid="sim-keeps-cleaning">
          <input
            type="checkbox"
            checked={keepsCleaning}
            onChange={(e) => setKeepsCleaning(e.target.checked)}
            className="accent-[#7A1535]"
          />
          <span>Le ménage me revient intégralement</span>
        </label>
      </div>

      {/* Breakdown */}
      <div className="mt-6 space-y-2 text-sm">
        <PayoutRow label="Voyageur paie" value={`${travelerPays.toFixed(2)} €`} muted />
        <div className="border-t border-black/10 pt-2" />
        <PayoutRow label="MSB (commission 10%)" value={`${msbFee.toFixed(2)} €`} muted small />
        <PayoutRow label={`Vous (${share}%${keepsCleaning ? " + ménage" : ""})`} value={`${conciergeCut.toFixed(2)} €`} accent />
        <PayoutRow label="Propriétaire" value={`${ownerPayout.toFixed(2)} €`} small muted />
        <div className="border-t border-black/10 pt-2" />
        <p className="text-[11px] text-neutral-600 leading-snug">
          Versement <strong>automatique sur votre compte Stripe</strong> à chaque réservation payée. Zéro relance.
        </p>
      </div>
    </div>
  );
}

function SimRow({ label, value, v, onChange, min, max, step, accent = false, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{label}</span>
        <span className={`font-display font-bold text-base ${accent ? "text-[#7A1535]" : "text-[#0A0A0A]"}`}>{value}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={v}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#7A1535] h-1"
      />
    </div>
  );
}
